import {
    GetSupplyOrderDetailsQuery,
    GetSupplyOrderProductsToAddQuery,
    SupplyOrderUpdateInput,
} from 'data/__generated__';

import { SupplyOrderProductReceivedDetail } from '../shared/types';

export type SupplyOrderDetails = GetSupplyOrderDetailsQuery['supplyOrder'];
export type SupplyOrderDetailsProduct = SupplyOrderDetails['products'][number];
export type SupplyOrderProductToAdd = GetSupplyOrderProductsToAddQuery['supplyOrderProductsToAdd'][number];

/**
 * Une ligne du tableau produits.
 *
 * Elle porte DEUX choses qu'il ne faut pas confondre :
 * - les champs **saisissables** (`colis`, `confirmedQuantity`, `unitPrice`, `productReceivedDetails`,
 *   `storedAt`), seuls à partir dans la mutation ;
 * - un **instantané d'affichage** (nom, colisage, stock, alertes…), lu par le tableau et jamais envoyé.
 *
 * Les deux vivent ensemble parce qu'on peut ajouter une ligne qui n'existe pas encore sur l'achat : elle
 * doit alors s'afficher avant d'avoir été sauvegardée. Le serveur, lui, redérive tout ce qui est calculé
 * (quantité, prix de ligne, libellé, TVA) — voir `resolve-supply-order-product-updates.function.ts`.
 */
export type SupplyOrderProductFormValue = {
    _id: string;
    colis: number;
    confirmedQuantity: number | null;
    unitPrice: number | null;
    productReceivedDetails: SupplyOrderProductReceivedDetail[];
    storedAt: number | null;

    // Instantané d'affichage — jamais soumis.
    availableStock: number;
    brand: string;
    conditionning: number;
    isAtSoldOutRisk: boolean;
    isToBeArchived: boolean;
    minSupplierOrderQuantity: number | null;
    name: string;
    packagesPerBatch: number;
    quantityGenerated: number;
    quantityGeneratedForMenus: number;
    stock: number;
    supplierShortageInfo: { inShortage: boolean; returnDate?: string | null };
    supplierUnitPrice: number;
    volume: string;
};

export type SupplyOrderFormValues = {
    comment: string | null;
    dateDelivery: string | null;
    dateDeliveryScheduled: string | null;
    description: string | null;
    hourDelivery: string | null;
    nextOrderDate: string | null;
    orderDate: string | null;
    priceHTInvoice: number | null;
    priceTTCInvoice: number | null;
    temperature: number | null;
    weeklyMenusStartDate: string | null;
    products: SupplyOrderProductFormValue[];
};

export function getProductFormValue(
    product: SupplyOrderDetailsProduct,
    soldOutRiskProductIds: readonly string[],
): SupplyOrderProductFormValue {
    return {
        _id: product._id,
        colis: product.colis,
        confirmedQuantity: product.confirmedQuantity ?? null,
        unitPrice: product.unitPrice,
        productReceivedDetails: product.productReceivedDetails.map(
            ({ brokenItems, colis, conditionning, expiryDate, quantity }) => ({
                brokenItems,
                colis,
                conditionning,
                expiryDate,
                quantity,
            }),
        ),
        storedAt: product.storedAt ?? null,

        availableStock: product.availableStock,
        brand: product.brand,
        conditionning: product.conditionning,
        isAtSoldOutRisk: soldOutRiskProductIds.includes(product._id),
        isToBeArchived: product.isToBeArchived,
        minSupplierOrderQuantity: product.minSupplierOrderQuantity ?? null,
        name: product.name,
        packagesPerBatch: product.packagesPerBatch,
        quantityGenerated: product.quantityGenerated,
        quantityGeneratedForMenus: product.quantityGeneratedForMenus,
        stock: product.stock,
        supplierShortageInfo: product.supplierShortageInfo,
        supplierUnitPrice: product.unitPrice,
        volume: product.volume,
    };
}

/** Une référence du catalogue fournisseur qu'on ajoute à l'achat : tout est à zéro, rien n'est encore commandé. */
export function getProductFormValueFromCatalogue(product: SupplyOrderProductToAdd): SupplyOrderProductFormValue {
    return {
        _id: product._id,
        colis: 0,
        confirmedQuantity: null,
        unitPrice: product.unitPrice,
        productReceivedDetails: [],
        storedAt: null,

        availableStock: product.availableStock,
        brand: product.brand,
        conditionning: product.conditionning,
        isAtSoldOutRisk: false,
        isToBeArchived: product.isToBeArchived,
        minSupplierOrderQuantity: null,
        name: product.name,
        packagesPerBatch: product.packagesPerBatch,
        quantityGenerated: 0,
        quantityGeneratedForMenus: 0,
        stock: product.stock,
        supplierShortageInfo: product.supplierShortageInfo,
        supplierUnitPrice: product.unitPrice,
        volume: product.volume,
    };
}

export function getSupplyOrderFormValues(supplyOrder: SupplyOrderDetails): SupplyOrderFormValues {
    return {
        comment: supplyOrder.comment ?? null,
        dateDelivery: supplyOrder.dateDelivery ?? null,
        dateDeliveryScheduled: supplyOrder.dateDeliveryScheduled ?? null,
        description: supplyOrder.description ?? null,
        hourDelivery: supplyOrder.hourDelivery ?? null,
        nextOrderDate: supplyOrder.nextOrderDate ?? null,
        orderDate: supplyOrder.orderDate ?? null,
        priceHTInvoice: supplyOrder.priceHTInvoice ?? null,
        priceTTCInvoice: supplyOrder.priceTTCInvoice ?? null,
        temperature: supplyOrder.temperature ?? null,
        weeklyMenusStartDate: supplyOrder.weeklyMenusStartDate ?? null,
        products: supplyOrder.products.map((product) =>
            getProductFormValue(product, supplyOrder.soldOutRiskProductIds),
        ),
    };
}

/**
 * Ce qui part réellement au serveur. Une ligne sans colis ni quantité confirmée disparaît de l'achat :
 * c'est ainsi qu'on retire un produit, en remettant son nombre de colis à zéro.
 */
export function mapFormValuesToUpdateInput(formValues: SupplyOrderFormValues): SupplyOrderUpdateInput {
    return {
        comment: formValues.comment,
        dateDelivery: formValues.dateDelivery,
        dateDeliveryScheduled: formValues.dateDeliveryScheduled,
        description: formValues.description,
        hourDelivery: formValues.hourDelivery,
        nextOrderDate: formValues.nextOrderDate,
        orderDate: formValues.orderDate,
        priceHTInvoice: formValues.priceHTInvoice,
        priceTTCInvoice: formValues.priceTTCInvoice,
        temperature: formValues.temperature,
        weeklyMenusStartDate: formValues.weeklyMenusStartDate,
        products: formValues.products
            .filter(({ colis, confirmedQuantity }) => colis || confirmedQuantity)
            .map(({ _id, colis, confirmedQuantity, productReceivedDetails, storedAt, unitPrice }) => ({
                _id,
                colis,
                confirmedQuantity,
                productReceivedDetails,
                storedAt,
                unitPrice,
            })),
    };
}

/** Quantité commandée : jamais saisie, toujours `colis × colisage` — la même règle que côté serveur. */
export function getOrderedQuantity(product: Pick<SupplyOrderProductFormValue, 'colis' | 'conditionning'>): number {
    return product.colis * product.conditionning;
}

/** Quantité qu'on attend vraiment : celle que le fournisseur a confirmée, à défaut celle commandée. */
export function getExpectedQuantity(
    product: Pick<SupplyOrderProductFormValue, 'colis' | 'conditionning' | 'confirmedQuantity'>,
): number {
    return product.confirmedQuantity ?? getOrderedQuantity(product);
}

export function getProductLinePrice(product: SupplyOrderProductFormValue): number {
    return getExpectedQuantity(product) * (product.unitPrice ?? product.supplierUnitPrice);
}
